import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../common/GenericTable'
import SearchWithFilter from '../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import SimpleButton from '../common/SimpleButton'
import ActionsMenu from '../common/ActionsMenu'
import Dropdown from '../common/Dropdown'
import { toast } from '../common/Toast'
import { useWindowSize } from '../../libs/hooks'
import { convertTimeWithTAndZ } from '../../utils/filters/date'
import { drAuthRules } from '../../utils/drConfig'
import { DisabledIcon, MenuKebabVerticalBackgroundIcon, MenuKebabVerticalIcon, PendingIcon } from '../../icons'
import { clearCorporateBranchMessages } from '../../actions/corporate'
import { cancelCompanyRequest, loadCompanyRequests } from '../../actions/userCompany'

const orderingFields = [
  {
    field: 'branch',
    order: 'asc',
    translationKey: 'branch_asc',
  },
  {
    field: 'branch',
    order: 'desc',
    translationKey: 'branch_desc',
  },
  {
    field: 'email',
    order: 'asc',
    translationKey: 'email_asc',
  },
  {
    field: 'email',
    order: 'desc',
    translationKey: 'email_desc',
  },
  {
    field: 'lastModifyAt',
    order: 'asc',
    translationKey: 'date_asc',
  },
  {
    field: 'lastModifyAt',
    order: 'desc',
    translationKey: 'date_desc',
  },
  {
    field: 'status',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'status',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Requests = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [tableData, setTableData] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [statusesFilter, setStatusesFilter] = useState([])
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [showSpinner, setShowSpinner] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [expandedRequest, setExpandedRequest] = useState(0)
  const requestsStatuses = ['invited', 'expired']

  useEffect(() => {
    const requests = props.requests.map((request, index) => ({
      index,
      email: request.email,
      branch: request.branchName,
      date: convertTimeWithTAndZ(request.lastModifyAt),
      status: request.status,
      actions: [
        ...(request.status === 'expired'
          ? [
              {
                label: 'resend',
                isDisabled: !drAuthRules.isCompanyAccountUpdateAllowed,
                onActionClick: (e) => {
                  e.stopPropagation()
                  handleChangeRequestStatus(request.id, 'resend')
                },
                // requiredPermission: organizationRequestsWritePermission,TODO
              },
            ]
          : []),
        ...(request.status === 'invited' || request.status === 'expired'
          ? [
              {
                label: request.status === 'invited' ? 'cancel' : 'remove',
                isDisabled: !drAuthRules.isCompanyAccountUpdateAllowed,
                onActionClick: (e) => {
                  e.stopPropagation()
                  handleChangeRequestStatus(request.id, 'cancel')
                },
                // requiredPermission: organizationRequestsWritePermission,TODO
              },
            ]
          : []),
      ],
    }))
    setTableData(requests)
  }, [props.requests])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)
      props.actions.clearCorporateBranchMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)
      props.actions.clearCorporateBranchMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadRequests(false)
    }
  }, [selectedPage])

  useEffect(() => {
    let delay = setTimeout(
      () => {
        handleLoadRequests(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => clearTimeout(delay)
  }, [searchParam, statusesFilter, orderBy])

  const handleLoadRequests = (withReset) => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }
    props.actions.loadCompanyRequests(queryParams).then(() => {
      setIsFirstLoad(false)
    })
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (statusesFilter.length > 0) {
      queryParams = { ...queryParams, status: statusesFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleSearch = (value) => {
    if (value?.length > 2 || (value?.length === 0 && searchParam)) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const selectOrderByOptions = (key) => {
    const option = orderingFields.find((o) => o.translationKey === key)
    handleSort(option)
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setStatusesFilter([])
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const handleChangeRequestStatus = (requestId, action) => {
    if (!props.companyId) {
      return
    }

    props.actions.cancelCompanyRequest(requestId, {
      action: action,
    })
  }

  const renderRequestStatus = (status) => {
    switch (status) {
      case requestsStatuses[0]:
        return (
          <div className="status-pending">
            <PendingIcon />
            <p>{t('invited')}</p>
          </div>
        )
      case requestsStatuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('expired')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="requests-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={handleSearch}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={handleResetFilters}
            showCounterBadge={statusesFilter.length > 0}
            counterBadge={statusesFilter.length}
          />
        </div>
        {isMobile && tableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && tableData.length > 0 && (
          <GenericTable
            data={tableData}
            headers={[
              {
                title: t('email'),
                orderKey: 'email',
              },
              {
                title: t('branch'),
                orderKey: 'branch',
              },
              {
                title: t('date'),
                orderKey: 'lastModifyAt',
              },
              {
                title: t('status'),
                orderKey: 'status',
              },
              {
                title: '',
              },
            ]}
            keys={['email', 'branch', 'date', 'status', 'action']}
            keyRenderer={{
              status: (item) => {
                return <div className="status-wrapper">{renderRequestStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            activeSort={orderBy}
            onSort={handleSort}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          tableData.map((request, index) => (
            <div key={index} className="card" onClick={() => setExpandedRequest(index)}>
              <div className={'card-item' + (expandedRequest !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('branch')}</div>
                <div className={'card-item-body' + (expandedRequest !== index ? ' align-center' : '')}>
                  <div>{request.branch}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={request.actions} isLoading={showSpinner} />
                    )}
                  </div>
                </div>
              </div>
              {expandedRequest === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('email')}</div>
                    <div className="card-item-body email-wrapper">{request.email}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('date')}</div>
                    <div className="card-item-body">{request.date}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('dealer-portal.request_status')}</div>
                    <div className="card-item-body card-item-body-status">{renderRequestStatus(request.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && tableData.length === 0 && (
          <EmptyPageResults title={t('dealer-portal.no_requests_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={handleLoadMore}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>

      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: requestsStatuses,
              input: statusesFilter,
              output: setStatusesFilter,
            },
          ]}
          shouldFilter={() => handleLoadRequests(true)}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </Fragment>
  )
}

function stateToProps({ userCompany }) {
  return {
    requests: userCompany?.companyRequests || [],
    isLoading: userCompany?.isLoading,
    canLoadMore: userCompany?.canLoadMore,
    errorMessage: userCompany?.errorMessage,
    successMessage: userCompany?.successMessage,
    companyId: userCompany?.companyId,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCompanyRequests,
        cancelCompanyRequest,
        clearCorporateBranchMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Requests)
